import React, { useState, useEffect } from 'react';
import {
  TrendingUp, BarChart2, AlertTriangle, Activity, Calendar, RefreshCw,
  Users, Shield, Brain, X, CheckCircle, Zap,
  Globe, Filter, Download, Play, Square, TrendingDown,
  ArrowUpDown, ChevronDown, ChevronUp, DollarSign, Eye,
  Target, Network, Cpu,
} from 'lucide-react';
import {
  ComposedChart, AreaChart, Area, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceArea, ReferenceLine,
} from 'recharts';
import { apiService } from '../services/apiService';
import type { MonitoringScope } from '../types';
import { API_BASE_URL } from '../config';
import { getAgencyProfile } from '../utils/agencyProfiles';

// ── Types ──────────────────────────────────────────────────────────────────
interface RiskPoint  { date: string; score: number | null; fScore?: number | null; event?: string | null }
interface SentPoint  { date: string; positive: number; neutral: number; negative: number; volume: number }
interface FreqPoint  { date: string; critical: number; warning: number; info: number }
interface Narrative  {
  id: string; claim: string; riskScore: number; velocity: number;
  platforms: string[]; status: string; reachEstimate: number;
  credibility: 'high' | 'medium' | 'low'; topFactors: string[];
}
interface AlertItem  { id: string; severity: string; title: string; claim: string; riskScore: number; velocity: number; platforms: string[]; actions: string[]; status: string }
interface AIInsight  { level: 'Low' | 'Elevated' | 'Critical'; summary: string; actions: string[]; confidence: number; signals: string[] }
interface GeoRegion  { name: string; x: number; y: number; risk: number; count: number }
interface Amplifier  { id: string; handle: string; followers: string; risk: number; platform: string; content: string }

// ── Rich Demo Data — June 9–16, 2026 coordinated campaign scenario ─────────
const MOCK_RISK: RiskPoint[] = [
  { date: '06/09', score: 42, fScore: null },
  { date: '06/10', score: 55, fScore: null },
  { date: '06/11', score: 61, fScore: null, event: 'Political Rally' },
  { date: '06/12', score: 78, fScore: null, event: 'Viral Health Claim' },
  { date: '06/13', score: 85, fScore: null, event: 'Coordinated Campaign' },
  { date: '06/14', score: 73, fScore: null },
  { date: '06/15', score: 67, fScore: null },
  { date: '06/16', score: 58, fScore: null },
];

const SIM_RISK: RiskPoint[] = [
  { date: '06/09', score: 28, fScore: null },
  { date: '06/10', score: 38, fScore: null },
  { date: '06/11', score: 62, fScore: null, event: 'Simulation Trigger' },
  { date: '06/12', score: 84, fScore: null, event: 'Narrative Surge' },
  { date: '06/13', score: 95, fScore: null, event: 'Crisis Peak' },
  { date: '06/14', score: 91, fScore: null },
  { date: '06/15', score: 79, fScore: null },
  { date: '06/16', score: 88, fScore: null },
];

const FORECAST_EXT = [
  { date: '06/17', score: null, fScore: 52 },
  { date: '06/18', score: null, fScore: 47 },
  { date: '06/19', score: null, fScore: 41 },
];

const MOCK_SENTIMENT: SentPoint[] = [
  { date: '06/09', positive: 45, neutral: 38, negative: 17, volume: 3200 },
  { date: '06/10', positive: 40, neutral: 35, negative: 25, volume: 4100 },
  { date: '06/11', positive: 32, neutral: 30, negative: 38, volume: 5800 },
  { date: '06/12', positive: 22, neutral: 28, negative: 50, volume: 8900 },
  { date: '06/13', positive: 18, neutral: 25, negative: 57, volume: 12400 },
  { date: '06/14', positive: 28, neutral: 32, negative: 40, volume: 7600 },
  { date: '06/15', positive: 35, neutral: 36, negative: 29, volume: 5200 },
  { date: '06/16', positive: 42, neutral: 37, negative: 21, volume: 3900 },
];

const MOCK_FREQ: FreqPoint[] = [
  { date: '06/09', critical: 2, warning: 5, info: 9 },
  { date: '06/10', critical: 3, warning: 7, info: 11 },
  { date: '06/11', critical: 5, warning: 9, info: 8 },
  { date: '06/12', critical: 8, warning: 14, info: 6 },
  { date: '06/13', critical: 11, warning: 16, info: 5 },
  { date: '06/14', critical: 7, warning: 11, info: 8 },
  { date: '06/15', critical: 4, warning: 8, info: 10 },
  { date: '06/16', critical: 3, warning: 6, info: 12 },
];

const MOCK_NARRATIVES: Narrative[] = [
  { id: '1', claim: 'Manipulated video of political leader circulating widely across social platforms', riskScore: 91, velocity: 520, platforms: ['TikTok', 'Twitter'], status: 'escalated', reachEstimate: 2100000, credibility: 'low', topFactors: ['High-velocity coordinated amplification', 'Deepfake visual indicators', 'Anonymous account network', 'Cross-platform sync pattern'] },
  { id: '2', claim: 'Unverified election integrity claims linked to forged government documents', riskScore: 87, velocity: 340, platforms: ['Twitter', 'Telegram'], status: 'investigating', reachEstimate: 1450000, credibility: 'low', topFactors: ['Document forgery signals detected', 'Sentiment spike +340%', 'Encrypted channel coordination', 'Low domain trust score'] },
  { id: '3', claim: 'Vaccine side-effect rumor spreading across health forums and Facebook groups', riskScore: 74, velocity: 180, platforms: ['Facebook', 'Reddit'], status: 'contained', reachEstimate: 890000, credibility: 'medium', topFactors: ['Medical misinformation patterns', 'High share-to-like ratio', 'Refuted by WHO and CDC', 'Emotional fear-based framing'] },
  { id: '4', claim: 'Financial scam targeting retail investors using fake news site network', riskScore: 69, velocity: 95, platforms: ['YouTube', 'Twitter'], status: 'new', reachEstimate: 420000, credibility: 'low', topFactors: ['Fake news site network detected', 'SEO manipulation fingerprint', 'Investment fraud pattern', 'Newsfeed injection technique'] },
  { id: '5', claim: 'False infrastructure damage reports circulating in crisis-affected regional groups', riskScore: 58, velocity: 62, platforms: ['Facebook', 'NewsAPI'], status: 'new', reachEstimate: 210000, credibility: 'medium', topFactors: ['Unverified emergency framing', 'Regional geographic cluster', 'Fear-based linguistic markers'] },
];

const MOCK_ALERTS: AlertItem[] = [
  { id: 'a1', severity: 'critical', title: 'Coordinated amplification detected', claim: 'Manipulated video of political leader spreading at 520 posts/hr across TikTok and Twitter', riskScore: 91, velocity: 520, platforms: ['TikTok', 'Twitter'], actions: ['Flag for fact-check', 'Notify partner outlets', 'Suppress amplification'], status: 'new' },
  { id: 'a2', severity: 'critical', title: 'High-velocity health misinformation', claim: 'Vaccine side-effect rumor: +340% velocity increase in 48h with cross-platform spread', riskScore: 87, velocity: 340, platforms: ['Facebook', 'Reddit'], actions: ['Activate health fact-check queue', 'Monitor medical influencer accounts', 'Prepare clarifying content'], status: 'investigating' },
  { id: 'a3', severity: 'warning', title: 'Election integrity narrative rising', claim: 'Forged document claims gaining traction via encrypted private channels', riskScore: 74, velocity: 180, platforms: ['Twitter', 'Telegram'], actions: ['Cross-reference with fact-check partners', 'Prepare rapid response messaging'], status: 'new' },
  { id: 'a4', severity: 'warning', title: 'Financial scam content escalating', claim: 'Fake news sites pushing investment fraud reaching 95k daily impressions', riskScore: 69, velocity: 95, platforms: ['YouTube'], actions: ['Report to platform moderation', 'Issue public advisory'], status: 'new' },
  { id: 'a5', severity: 'info', title: 'Infrastructure rumor — monitoring', claim: 'Unverified emergency reports circulating in regional community groups', riskScore: 58, velocity: 62, platforms: ['Facebook'], actions: ['Continue monitoring', 'Request official clarification from authorities'], status: 'contained' },
];

// ── Phase 2 & 3 data ────────────────────────────────────────────────────────
const GEO_REGIONS: GeoRegion[] = [
  { name: 'North America', x: 140, y: 125, risk: 45, count: 12 },
  { name: 'South America', x: 207, y: 272, risk: 38, count: 7 },
  { name: 'W. Europe',     x: 432, y: 102, risk: 62, count: 18 },
  { name: 'E. Europe',     x: 508, y: 93,  risk: 71, count: 15 },
  { name: 'Middle East',   x: 553, y: 172, risk: 78, count: 22 },
  { name: 'N. Africa',     x: 462, y: 188, risk: 61, count: 16 },
  { name: 'Sub-Saharan',   x: 476, y: 263, risk: 55, count: 11 },
  { name: 'South Asia',    x: 630, y: 192, risk: 85, count: 29 },
  { name: 'East Asia',     x: 710, y: 138, risk: 67, count: 19 },
  { name: 'SE Asia',       x: 692, y: 227, risk: 59, count: 14 },
  { name: 'Oceania',       x: 740, y: 312, risk: 28, count: 4  },
  { name: 'Central Asia',  x: 600, y: 118, risk: 44, count: 8  },
];

const MOCK_AMPLIFIERS: Amplifier[] = [
  { id: '1', handle: '@crisis_network_77',    followers: '2.1M', risk: 94, platform: 'Twitter',  content: 'Coordinated political narrative amplification' },
  { id: '2', handle: '@truth_wave_official',  followers: '1.4M', risk: 89, platform: 'TikTok',   content: 'Health misinformation — cross-platform sync' },
  { id: '3', handle: '@breakingreport_real',  followers: '890K', risk: 84, platform: 'Telegram', content: 'Election integrity fabricated documents' },
  { id: '4', handle: '@viralhealth_info',     followers: '620K', risk: 77, platform: 'Facebook', content: 'Vaccine rumor distribution node' },
  { id: '5', handle: '@investornews_alert',   followers: '380K', risk: 69, platform: 'YouTube',  content: 'Financial scam content promoter' },
];

// ── Helpers ────────────────────────────────────────────────────────────────
function Sparkline({ scores, color = '#818cf8' }: { scores: number[]; color?: string }) {
  if (scores.length < 2) return null;
  const max = Math.max(...scores);
  const min = Math.min(...scores);
  const range = max - min || 1;
  const W = 56, H = 20;
  const pts = scores
    .map((v, i) => `${((i / (scores.length - 1)) * W).toFixed(1)},${(H - ((v - min) / range) * H).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={W} height={H}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function getThreatLevel(avgRisk: number) {
  if (avgRisk >= 70) return {
    level: 'Critical',
    color: 'text-red-400',
    pill: 'bg-red-500/15 border border-red-500/40 text-red-400',
    dot: 'bg-red-400',
    desc: 'Active coordinated campaign — immediate review recommended',
  };
  if (avgRisk >= 45) return {
    level: 'Elevated',
    color: 'text-amber-400',
    pill: 'bg-amber-500/15 border border-amber-500/40 text-amber-400',
    dot: 'bg-amber-400',
    desc: 'Monitoring increased activity — narratives require attention',
  };
  return {
    level: 'Low',
    color: 'text-emerald-400',
    pill: 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-400',
    dot: 'bg-emerald-400',
    desc: 'Stable information environment — no active high-risk campaigns',
  };
}

function generateInsight(_riskData: RiskPoint[], freqData: FreqPoint[], avgRisk: number): AIInsight {
  const criticalCount = freqData.reduce((s, d) => s + d.critical, 0);
  if (avgRisk >= 65 || criticalCount > 5) {
    return {
      level: 'Critical',
      summary:
        'Primary risk vector this week: Coordinated amplification of unverified political claims originating from 3 high-velocity source accounts. Velocity increased +340% in last 48h. Estimated potential reach if unchecked: 4.8M users.',
      actions: [
        'Monitor top amplifier handles and cross-reference with fact-check partners',
        'Prepare rapid response messaging for partner media outlets',
        'Flag top 4 posts for platform reporting and suppression',
      ],
      confidence: 0.87,
      signals: [
        'High-velocity coordinated amplification',
        'Cross-platform narrative synchronization',
        'Low source credibility score',
        'Negative sentiment surge (+57%)',
      ],
    };
  }
  if (avgRisk >= 45) {
    return {
      level: 'Elevated',
      summary:
        'Primary risk vector: Emerging health-related narratives showing early signs of cross-platform spread. Current velocity moderate but increasing 18% day-over-day. Estimated reach if unchecked: 890K users.',
      actions: [
        'Activate health fact-check queue for top narratives',
        'Monitor high-influence accounts in the health space',
        'Prepare clarifying content with official health authorities',
      ],
      confidence: 0.79,
      signals: ['Rising sentiment negativity', 'Multi-platform spread patterns', 'Moderate amplification velocity'],
    };
  }
  return {
    level: 'Low',
    summary:
      'Overall information environment remains stable. No high-velocity coordinated campaigns detected in the selected period. 2 low-severity alerts were auto-resolved. Your monitoring posture is effective.',
    actions: [
      'Continue routine monitoring schedule',
      'Review watch-list narratives weekly',
      'Update fact-check partner integrations',
    ],
    confidence: 0.92,
    signals: ['Low risk score trend', 'Positive sentiment recovery', 'Reduced alert velocity'],
  };
}

const TOOLTIP_STYLE = {
  contentStyle: { background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px', color: '#e4e4e7', fontSize: '12px' },
  labelStyle: { color: '#a1a1aa' },
  itemStyle: { color: '#e4e4e7' },
};

const STATUS_COLOR: Record<string, string> = {
  escalated:    'text-red-400 bg-red-500/10 border border-red-500/20',
  investigating:'text-amber-400 bg-amber-500/10 border border-amber-500/20',
  new:          'text-violet-400 bg-violet-500/10 border border-violet-500/20',
  contained:    'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20',
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-400 border border-red-500/30',
  warning:  'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  info:     'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30',
};

// ── Phase 2: Geographic Risk Map ────────────────────────────────────────────
function GeoRiskMap({ regions }: { regions: GeoRegion[] }) {
  const [hovered, setHovered] = useState<GeoRegion | null>(null);
  const W = 800, H = 380;
  return (
    <div className="relative w-full rounded-xl overflow-hidden" style={{ aspectRatio: `${W}/${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
        <rect width={W} height={H} fill="#0c1220" rx="8" />
        {[...Array(8)].map((_, i) => <line key={`h${i}`} x1={0} y1={(i+1)*H/9} x2={W} y2={(i+1)*H/9} stroke="#1a2744" strokeWidth={0.5} />)}
        {[...Array(16)].map((_, i) => <line key={`v${i}`} x1={(i+1)*W/17} y1={0} x2={(i+1)*W/17} y2={H} stroke="#1a2744" strokeWidth={0.5} />)}
        {/* Equator */}
        <line x1={0} y1={H*0.52} x2={W} y2={H*0.52} stroke="#1e3a5f" strokeWidth={1} strokeDasharray="4 4" />
        {/* Approximate continents */}
        <polygon points="78,58 242,58 252,202 182,228 88,198 68,138" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        <polygon points="163,228 252,228 262,342 202,360 153,298" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        <polygon points="378,58 552,58 557,142 502,162 378,152" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        <polygon points="413,152 557,152 562,312 487,352 408,312" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        <polygon points="518,128 662,108 672,212 537,217 503,177" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        <polygon points="578,148 762,128 777,262 692,292 568,232" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        <polygon points="678,283 772,283 777,352 692,362 673,332" fill="#162035" stroke="#1e3050" strokeWidth={0.8} />
        {/* Risk bubbles */}
        {regions.map(r => {
          const color = r.risk >= 70 ? '#f43f5e' : r.risk >= 45 ? '#f59e0b' : '#22c55e';
          const radius = Math.sqrt(r.count) * 4.5 + 7;
          const isHov = hovered?.name === r.name;
          return (
            <g key={r.name} style={{ cursor: 'pointer' }} onMouseEnter={() => setHovered(r)} onMouseLeave={() => setHovered(null)}>
              <circle cx={r.x} cy={r.y} r={radius + 8} fill={color} fillOpacity={isHov ? 0.18 : 0.07} />
              <circle cx={r.x} cy={r.y} r={radius} fill={color} fillOpacity={0.22} stroke={color} strokeWidth={isHov ? 2 : 1.5} />
              <text x={r.x} y={r.y + 1} textAnchor="middle" dominantBaseline="middle" fill={color} fontSize={8} fontWeight="700">{r.risk}</text>
              {!isHov && <text x={r.x} y={r.y + radius + 10} textAnchor="middle" fill="#4b6080" fontSize={6.5}>{r.name}</text>}
            </g>
          );
        })}
        {/* Hover tooltip */}
        {hovered && (() => {
          const tx = Math.min(Math.max(hovered.x - 60, 4), W - 128);
          const ty = Math.max(hovered.y - 58, 4);
          const color = hovered.risk >= 70 ? '#f43f5e' : hovered.risk >= 45 ? '#f59e0b' : '#22c55e';
          return (
            <g>
              <rect x={tx} y={ty} width={124} height={50} rx={5} fill="#1e293b" stroke="#334155" />
              <text x={tx+8} y={ty+16} fill="white" fontSize={9} fontWeight="600">{hovered.name}</text>
              <text x={tx+8} y={ty+30} fill="#94a3b8" fontSize={8}>Risk: <tspan fill={color} fontWeight="700">{hovered.risk}</tspan></text>
              <text x={tx+8} y={ty+43} fill="#94a3b8" fontSize={8}>Active alerts: {hovered.count}</text>
            </g>
          );
        })()}
        {/* Legend */}
        <circle cx={18} cy={H-36} r={5} fill="#f43f5e" fillOpacity={0.5} stroke="#f43f5e" strokeWidth={1} />
        <text x={28} y={H-32} fill="#64748b" fontSize={8}>Critical ≥70</text>
        <circle cx={18} cy={H-20} r={5} fill="#f59e0b" fillOpacity={0.5} stroke="#f59e0b" strokeWidth={1} />
        <text x={28} y={H-16} fill="#64748b" fontSize={8}>Elevated 45–70</text>
        <circle cx={105} cy={H-36} r={5} fill="#22c55e" fillOpacity={0.5} stroke="#22c55e" strokeWidth={1} />
        <text x={115} y={H-32} fill="#64748b" fontSize={8}>Low &lt;45</text>
        <text x={105} y={H-16} fill="#374151" fontSize={7}>Size ∝ alert count</text>
      </svg>
    </div>
  );
}

// ── Phase 2: Narrative Detail Modal ────────────────────────────────────────
function NarrativeModal({ narrative, onClose, onStatusChange }: {
  narrative: Narrative;
  onClose: () => void;
  onStatusChange: (id: string, status: string) => void;
}) {
  const nextStatuses: Record<string, string[]> = {
    new: ['investigating', 'contained', 'escalated'],
    investigating: ['contained', 'escalated'],
    escalated: ['investigating', 'contained'],
    contained: ['new'],
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700/50 rounded-xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-zinc-800">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLOR[narrative.status] ?? ''}`}>{narrative.status}</span>
              <span className="text-xs text-zinc-500">Narrative #{narrative.id}</span>
            </div>
            <p className="text-white font-semibold leading-snug">{narrative.claim}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-3 divide-x divide-zinc-800 border-b border-zinc-800">
          <div className="p-4 text-center">
            <div className={`text-2xl font-bold ${narrative.riskScore >= 80 ? 'text-red-400' : narrative.riskScore >= 60 ? 'text-amber-400' : 'text-emerald-400'}`}>{narrative.riskScore}</div>
            <div className="text-zinc-500 text-xs mt-0.5">Risk Score</div>
          </div>
          <div className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{narrative.velocity}<span className="text-sm font-normal">/hr</span></div>
            <div className="text-zinc-500 text-xs mt-0.5">Spread Velocity</div>
          </div>
          <div className="p-4 text-center">
            <div className="text-2xl font-bold text-violet-400">
              {narrative.reachEstimate >= 1000000 ? `${(narrative.reachEstimate/1000000).toFixed(1)}M` : `${(narrative.reachEstimate/1000).toFixed(0)}K`}
            </div>
            <div className="text-zinc-500 text-xs mt-0.5">Est. Reach</div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2">Active Platforms</p>
            <div className="flex flex-wrap gap-2">{narrative.platforms.map(p => <span key={p} className="text-sm bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1 rounded-full">{p}</span>)}</div>
          </div>
          <div>
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5"><Cpu className="w-3 h-3" />Model Explainability — Top Risk Factors</p>
            <div className="space-y-2.5">
              {narrative.topFactors.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1 bg-zinc-800 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-violet-500 transition-all" style={{ width: `${Math.max(30, 100 - i * 17)}%` }} />
                  </div>
                  <span className="text-zinc-300 text-xs w-60 flex-shrink-0">{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2">Source Credibility</p>
            <span className={`text-sm px-3 py-1 rounded-full font-medium ${narrative.credibility === 'low' ? 'bg-red-500/15 text-red-400' : narrative.credibility === 'medium' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
              {narrative.credibility === 'low' ? 'Low — Unverified / Anonymous Sources' : narrative.credibility === 'medium' ? 'Medium — Mixed Source Credibility' : 'High — Credible Source Pattern'}
            </span>
          </div>
          {(nextStatuses[narrative.status] ?? []).length > 0 && (
            <div>
              <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2">Update Workflow Status</p>
              <div className="flex flex-wrap gap-2">
                {(nextStatuses[narrative.status] ?? []).map(s => (
                  <button key={s} onClick={() => { onStatusChange(narrative.id, s); onClose(); }}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium capitalize border transition-colors ${s === 'contained' ? 'border-emerald-600/40 text-emerald-400 hover:bg-emerald-500/10' : s === 'escalated' ? 'border-red-600/40 text-red-400 hover:bg-red-500/10' : 'border-violet-600/40 text-violet-400 hover:bg-violet-500/10'}`}>
                    Mark as {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Phase 3: ROI Calculator ────────────────────────────────────────────────
function ROICalculator() {
  const [orgType, setOrgType] = useState('Government');
  const [networkSize, setNetworkSize] = useState(1000000);
  const [incidentsPerMonth, setIncidentsPerMonth] = useState(8);
  const [costPerIncident, setCostPerIncident] = useState(75000);
  const reductionRate = orgType === 'Government' ? 0.72 : orgType === 'Media' ? 0.68 : orgType === 'Enterprise' ? 0.65 : 0.70;
  const incidentsPrevented = Math.round(incidentsPerMonth * reductionRate);
  const annualSavings = incidentsPrevented * 12 * costPerIncident;
  const reachProtected = Math.round(networkSize * 0.38);
  const fmtMoney = (n: number) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;
  const fmtNum = (n: number) => n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : `${(n/1000).toFixed(0)}K`;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Organization Type', el: <select value={orgType} onChange={e => setOrgType(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/50">{['Government','Media','NGO','Enterprise'].map(t => <option key={t}>{t}</option>)}</select> },
          { label: 'Network Audience', el: <input type="number" value={networkSize} onChange={e => setNetworkSize(Number(e.target.value))} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/50" /> },
          { label: 'Incidents / Month', el: <input type="number" value={incidentsPerMonth} onChange={e => setIncidentsPerMonth(Number(e.target.value))} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/50" /> },
          { label: 'Cost / Incident ($)', el: <input type="number" value={costPerIncident} onChange={e => setCostPerIncident(Number(e.target.value))} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/50" /> },
        ].map(({ label, el }) => (
          <div key={label}>
            <label className="block text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-1.5">{label}</label>
            {el}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-emerald-400">{incidentsPrevented}<span className="text-lg">/mo</span></div>
          <div className="text-zinc-400 text-xs mt-1">Incidents Prevented</div>
          <div className="text-zinc-600 text-xs mt-0.5">{Math.round(reductionRate*100)}% reduction rate</div>
        </div>
        <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-violet-400">{fmtNum(reachProtected)}</div>
          <div className="text-zinc-400 text-xs mt-1">Users Protected</div>
          <div className="text-zinc-600 text-xs mt-0.5">38% of your network</div>
        </div>
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{fmtMoney(annualSavings)}</div>
          <div className="text-zinc-400 text-xs mt-1">Est. Annual Savings</div>
          <div className="text-zinc-600 text-xs mt-0.5">Conservative estimate</div>
        </div>
      </div>
    </div>
  );
}

// ── Phase 3: Top Amplifiers Panel ──────────────────────────────────────────
function AmplifiersPanel({ amplifiers }: { amplifiers: Amplifier[] }) {
  return (
    <div className="space-y-2">
      {amplifiers.map((a, i) => (
        <div key={a.id} className="flex items-center gap-3 p-3 bg-zinc-800/40 border border-zinc-700/40 rounded-lg hover:bg-zinc-800/70 transition-colors">
          <span className="text-zinc-600 text-xs font-bold w-4">#{i+1}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-violet-400 text-sm font-mono">{a.handle}</span>
              <span className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-400 px-1.5 py-0.5 rounded">{a.platform}</span>
            </div>
            <p className="text-zinc-500 text-xs truncate">{a.content}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className={`text-sm font-bold ${a.risk >= 85 ? 'text-red-400' : a.risk >= 70 ? 'text-amber-400' : 'text-emerald-400'}`}>{a.risk}</div>
            <div className="text-zinc-600 text-xs">{a.followers}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
interface AnalyticsProps {
  monitoringScope?: MonitoringScope;
}

const Analytics: React.FC<AnalyticsProps> = ({ monitoringScope }) => {
  const today   = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

  // ── Core data ─────────────────────────────────────────────────────────────
  const [startDate, setStartDate]             = useState(weekAgo);
  const [endDate, setEndDate]                 = useState(today);
  const [loading, setLoading]                 = useState(true);
  const [riskData, setRiskData]               = useState<RiskPoint[]>(MOCK_RISK);
  const [sentData, setSentData]               = useState<SentPoint[]>(MOCK_SENTIMENT);
  const [freqData, setFreqData]               = useState<FreqPoint[]>(MOCK_FREQ);

  // ── Phase 1 state ─────────────────────────────────────────────────────────
  const [selectedAlertDate, setSelectedAlertDate] = useState<string | null>(null);

  // ── Phase 2 state ─────────────────────────────────────────────────────────
  const [filterPlatform, setFilterPlatform]   = useState('All');
  const [filterTier, setFilterTier]           = useState('All');
  const [filterStatus, setFilterStatus]       = useState('All');
  const [sortBy, setSortBy]                   = useState<'riskScore' | 'velocity' | 'reachEstimate'>('riskScore');
  const [sortDir, setSortDir]                 = useState<'asc' | 'desc'>('desc');
  const [selectedNarrative, setSelectedNarrative] = useState<Narrative | null>(null);
  const [narrativeStatuses, setNarrativeStatuses] = useState<Record<string, string>>({});
  const [alertStatuses, setAlertStatuses]     = useState<Record<string, string>>({});

  // ── Phase 3 state ─────────────────────────────────────────────────────────
  const [simActive, setSimActive]             = useState(false);
  const [showForecast, setShowForecast]       = useState(false);
  const [showROI, setShowROI]                 = useState(false);
  const [showAmplifiers, setShowAmplifiers]   = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [riskRaw, sentRaw, freqRaw] = await Promise.allSettled([
        apiService.metrics.getRiskTrend(7, monitoringScope),
        apiService.metrics.getSentimentTrend(7, monitoringScope),
        apiService.metrics.getAlertTrend(7, monitoringScope),
      ]);
      const mapDate = (arr: any[]) =>
        arr.map((r: any) => ({ ...r, date: (r.date || r.timestamp || '').slice(5, 10) }));

      if (riskRaw.status === 'fulfilled' && Array.isArray(riskRaw.value) && riskRaw.value.length > 0)
        setRiskData(mapDate(riskRaw.value).map((r: any) => ({
          date: r.date, score: Math.round((r.average_risk ?? r.avg_risk ?? r.score ?? 0) * 100), fScore: null, event: r.event ?? null,
        })));

      if (sentRaw.status === 'fulfilled' && Array.isArray(sentRaw.value) && sentRaw.value.length > 0)
        setSentData(mapDate(sentRaw.value).map((r: any) => {
          const total = (r.positive ?? 0) + (r.neutral ?? 0) + (r.negative ?? 0) || 100;
          return { date: r.date, positive: Math.round((r.positive ?? 0)/total*100), neutral: Math.round((r.neutral ?? 0)/total*100), negative: Math.round((r.negative ?? 0)/total*100), volume: r.volume ?? 0 };
        }));

      if (freqRaw.status === 'fulfilled' && Array.isArray(freqRaw.value) && freqRaw.value.length > 0)
        setFreqData(mapDate(freqRaw.value));
    } catch { /* keep mocks */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [monitoringScope]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const activeRiskData = simActive ? SIM_RISK : riskData;
  const chartRiskData: RiskPoint[] = showForecast
    ? [
        ...activeRiskData.map((d, i) => i === activeRiskData.length - 1 ? { ...d, fScore: d.score } : d),
        ...FORECAST_EXT,
      ]
    : activeRiskData;

  const avgRisk     = Math.round(activeRiskData.reduce((s, d) => s + (d.score ?? 0), 0) / activeRiskData.length);
  const criticals   = freqData.reduce((s, d) => s + d.critical, 0);
  const totalAlerts = freqData.reduce((s, d) => s + d.critical + d.warning + d.info, 0);
  const threat      = getThreatLevel(simActive ? Math.max(avgRisk, 82) : avgRisk);
  const insight     = generateInsight(activeRiskData as any, freqData, simActive ? 82 : avgRisk);

  const normalizedSent = sentData.map(d => {
    const total = (d.positive + d.neutral + d.negative) || 100;
    return { ...d, positive: Math.round(d.positive/total*100), neutral: Math.round(d.neutral/total*100), negative: Math.round(d.negative/total*100) };
  });

  // ── Filtered + sorted narratives ──────────────────────────────────────────
  const allNarratives = MOCK_NARRATIVES.map(n => ({ ...n, status: narrativeStatuses[n.id] ?? n.status }));
  const filteredNarratives = allNarratives
    .filter(n => filterPlatform === 'All' || n.platforms.includes(filterPlatform))
    .filter(n => filterTier === 'All' || (filterTier === 'Critical' ? n.riskScore >= 70 : filterTier === 'Elevated' ? n.riskScore >= 45 && n.riskScore < 70 : n.riskScore < 45))
    .filter(n => filterStatus === 'All' || n.status === filterStatus)
    .sort((a, b) => { const d = (a[sortBy] as number) - (b[sortBy] as number); return sortDir === 'desc' ? -d : d; });

  const allAlerts = MOCK_ALERTS.map(a => ({ ...a, status: alertStatuses[a.id] ?? a.status }));

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const exportCSV = () => {
    const headers = ['Claim','Risk Score','Velocity (/hr)','Platforms','Status','Est. Reach'];
    const rows = allNarratives.map(n => [
      `"${n.claim}"`, n.riskScore, n.velocity, n.platforms.join('; '), n.status,
      n.reachEstimate >= 1000000 ? `${(n.reachEstimate/1000000).toFixed(1)}M` : `${(n.reachEstimate/1000).toFixed(0)}K`,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'crisishield-narratives.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Fetch the print-ready executive brief (auth-protected) and open it in a new
  // tab so the user can Save-as-PDF. Additive only — no core pipeline touched.
  const downloadBrief = async () => {
    try {
      const token = localStorage.getItem('access_token');
      let brand = 'CrisisShield';
      try {
        const raw = localStorage.getItem('user_profile');
        const orgType = raw ? (JSON.parse(raw)?.organization_type as string | undefined) : undefined;
        brand = getAgencyProfile(orgType).brand;
      } catch { /* keep default brand */ }
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate, brand });
      const res = await fetch(`${API_BASE_URL}/api/export/brief.html?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Brief export failed (${res.status})`);
      const html = await res.text();
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Brief export failed:', err);
      alert('Could not generate the brief. Please try again.');
    }
  };

  const PLATFORMS = ['All','Twitter','Facebook','TikTok','Reddit','YouTube','Telegram','NewsAPI'];
  const TIERS     = ['All','Critical','Elevated','Low'];
  const STATUSES  = ['All','new','investigating','escalated','contained'];

  return (
    <div className="p-6 space-y-6">

      {/* ── Phase 3: Simulation Banner ──────────────────────────────────── */}
      {simActive && (
        <div className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-red-400 rounded-full animate-ping" />
            <span className="text-red-400 font-semibold text-sm">SIMULATION MODE ACTIVE</span>
            <span className="text-red-300/70 text-sm hidden sm:block">Crisis wave scenario — data is simulated</span>
          </div>
          <button onClick={() => setSimActive(false)} className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-1.5 rounded-lg border border-red-500/30 transition-colors">
            End Simulation
          </button>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics Engine</h1>
          <p className="text-zinc-400 text-sm mt-1">Risk trends, sentiment analysis, and alert frequency</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Time presets */}
          <div className="flex items-center bg-zinc-800/60 border border-zinc-700/40 rounded-lg p-1 gap-0.5">
            {[['7D',7],['30D',30],['90D',90]].map(([label, days]) => (
              <button key={label} onClick={() => { setStartDate(new Date(Date.now() - (days as number)*86400000).toISOString().split('T')[0]); setEndDate(today); }}
                className="px-3 py-1 text-xs rounded-md text-zinc-400 hover:text-white hover:bg-zinc-700/60 transition-colors">
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-zinc-400" />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="bg-zinc-800/80 border border-zinc-700/50 text-white rounded-lg px-3 py-1.5 text-sm [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-violet-500/50" />
            <span className="text-zinc-400 text-sm">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="bg-zinc-800/80 border border-zinc-700/50 text-white rounded-lg px-3 py-1.5 text-sm [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-violet-500/50" />
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm transition-colors">
            <RefreshCw className="w-4 h-4" /> Apply
          </button>
          {/* Phase 3 action buttons */}
          <button onClick={() => setSimActive(s => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${simActive ? 'bg-red-500/15 border-red-500/40 text-red-400' : 'bg-zinc-800/60 border-zinc-700/40 text-zinc-300 hover:border-violet-500/40 hover:text-violet-300'}`}>
            {simActive ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {simActive ? 'Stop Sim' : 'Live Sim'}
          </button>
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800/60 border border-zinc-700/40 hover:border-emerald-500/40 text-zinc-300 hover:text-emerald-400 rounded-lg text-sm transition-colors">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button onClick={downloadBrief}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800/60 border border-zinc-700/40 hover:border-cyan-500/40 text-zinc-300 hover:text-cyan-400 rounded-lg text-sm transition-colors">
            <Download className="w-3.5 h-3.5" /> Brief
          </button>
        </div>
      </div>

      {/* ── KPI Row — 5 impact-focused cards ────────────────────────────── */}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-zinc-400" /><span className="text-zinc-400 text-xs">Overall Threat Level</span></div>
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold ${threat.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${threat.dot} animate-pulse`} />{threat.level}
          </div>
          <p className="text-zinc-500 text-xs mt-2 leading-tight">{threat.desc}</p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-zinc-400" /><span className="text-zinc-400 text-xs">Reach Protected</span></div>
          <div className="text-2xl font-bold text-violet-400">{simActive ? '7.2M' : '4.8M'}</div>
          <p className="text-zinc-500 text-xs mt-1">users shielded from high-risk narratives</p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-zinc-400" /><span className="text-zinc-400 text-xs">Narratives</span></div>
          <div className="text-2xl font-bold text-amber-400">{simActive ? '12 detected' : `${MOCK_NARRATIVES.length} detected`}</div>
          <p className="text-zinc-500 text-xs mt-1">{simActive ? '3 contained · 9 active' : '3 contained · 2 active'}</p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-zinc-400" /><span className="text-zinc-400 text-xs">Detection Accuracy</span></div>
          <div className="flex items-end gap-3">
            <div className="text-2xl font-bold text-emerald-400">94.2%</div>
            <Sparkline scores={[92, 93, 94, 93.5, 94.2]} color="#34d399" />
          </div>
          <p className="text-zinc-500 text-xs mt-1">validated · 1,247 ground-truth cases</p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Zap className="w-4 h-4 text-zinc-400" /><span className="text-zinc-400 text-xs">Avg Response Time</span></div>
          <div className="flex items-end gap-3">
            <div className="text-2xl font-bold text-cyan-400">1.3s</div>
            <Sparkline scores={[1.8, 1.6, 1.4, 1.3, 1.3]} color="#22d3ee" />
          </div>
          <p className="text-zinc-500 text-xs mt-1">p95: 2.1s · ↓18% vs last week</p>
        </div>
      </div>

      {/* ── AI Insight & Recommendation Panel ───────────────────────────── */}
      <div className="glass-panel rounded-xl p-5 border-l-4 border-violet-500">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Brain className="w-5 h-5 text-violet-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="text-white font-semibold">AI Insight &amp; Recommendations</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${insight.level === 'Critical' ? 'bg-red-500/15 text-red-400' : insight.level === 'Elevated' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                {insight.level} Risk
              </span>
              <span className="text-xs text-zinc-500 ml-auto">Confidence: {Math.round(insight.confidence * 100)}%</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">{insight.summary}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2">Recommended Actions</p>
                <ul className="space-y-1.5">
                  {insight.actions.map((action, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle className="w-3.5 h-3.5 text-violet-400 mt-0.5 flex-shrink-0" />{action}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2">Top Contributing Signals</p>
                <div className="flex flex-wrap gap-1.5">
                  {insight.signals.map((sig, i) => (
                    <span key={i} className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 px-2 py-1 rounded">{sig}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts ──────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-64 bg-zinc-800/50 animate-pulse rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-6">

          {/* Risk Score Trend — zones + event annotations + forecast toggle */}
          <div className="glass-panel rounded-xl p-5">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-rose-400" />
                <h2 className="text-white font-semibold">Risk Score Trend</h2>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Phase 3: Forecast toggle */}
                <button onClick={() => setShowForecast(f => !f)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${showForecast ? 'bg-violet-500/15 border-violet-500/40 text-violet-400' : 'bg-zinc-800/60 border-zinc-700/40 text-zinc-400 hover:border-violet-500/40 hover:text-violet-400'}`}>
                  <TrendingDown className="w-3 h-3" />{showForecast ? 'Hide Forecast' : 'Forecast +3d'}
                </button>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5 text-emerald-400"><span className="w-2 h-2 rounded-sm bg-emerald-500/30 inline-block" />Safe &lt;30</span>
                  <span className="flex items-center gap-1.5 text-amber-400"><span className="w-2 h-2 rounded-sm bg-amber-500/30 inline-block" />Caution 30–70</span>
                  <span className="flex items-center gap-1.5 text-red-400"><span className="w-2 h-2 rounded-sm bg-red-500/30 inline-block" />Critical &gt;70</span>
                </div>
              </div>
            </div>
            <p className="text-zinc-500 text-xs mb-4">
              Period avg: <span className={`font-semibold ${threat.color}`}>{avgRisk}</span>
              &nbsp;· Purple dashed lines mark significant events
              {showForecast && <span className="text-violet-400/70"> · Blue dashed area = AI forecast</span>}
            </p>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartRiskData} margin={{ top: 10, right: 16, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="riskGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.45} />
                    <stop offset="55%" stopColor="#f59e0b" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818cf8" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#818cf8" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis dataKey="date" stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <YAxis domain={[0, 100]} stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v: any, name: string) => [v != null ? `${v}` : '—', name === 'fScore' ? 'Forecast' : 'Risk Score']} />
                <ReferenceArea y1={0}  y2={30}  fill="#22c55e" fillOpacity={0.07} />
                <ReferenceArea y1={30} y2={70}  fill="#f59e0b" fillOpacity={0.06} />
                <ReferenceArea y1={70} y2={100} fill="#f43f5e" fillOpacity={0.09} />
                {chartRiskData.filter(d => d.event && d.score != null).map(d => (
                  <ReferenceLine key={d.date} x={d.date} stroke="#818cf8" strokeDasharray="4 2"
                    label={{ value: d.event as string, position: 'top', fill: '#818cf8', fontSize: 9 }} />
                ))}
                <Area type="monotone" dataKey="score" stroke="#f43f5e" fill="url(#riskGrad)" strokeWidth={2.5}
                  dot={{ fill: '#f43f5e', r: 3, strokeWidth: 0 }} activeDot={{ r: 5, fill: '#f43f5e', strokeWidth: 0 }}
                  connectNulls={false} />
                {showForecast && (
                  <Area type="monotone" dataKey="fScore" stroke="#818cf8" strokeDasharray="5 3" fill="url(#forecastGrad)"
                    strokeWidth={2} dot={{ fill: '#818cf8', r: 2.5, strokeWidth: 0 }}
                    activeDot={{ r: 4, fill: '#818cf8', strokeWidth: 0 }} connectNulls={false} name="fScore" />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Sentiment Analysis Trend — 100% stacked + volume secondary axis */}
          <div className="glass-panel rounded-xl p-5">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-violet-400" />
                <h2 className="text-white font-semibold">Sentiment Analysis Trend</h2>
              </div>
              <span className="text-xs text-zinc-500">Dashed line = daily posts analyzed (right axis)</span>
            </div>
            <p className="text-zinc-500 text-xs mb-4">100% stacked proportions · Hover bars for exact volumes</p>
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={normalizedSent} margin={{ top: 8, right: 44, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis dataKey="date" stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <YAxis yAxisId="pct" domain={[0, 100]} stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <YAxis yAxisId="vol" orientation="right" stroke="#6366f1" tick={{ fill: '#6366f1', fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : String(v)} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(val: number, name: string) => name === 'volume' ? [`${val.toLocaleString()} posts`, 'Volume'] : [`${val}%`, name]} />
                <Legend wrapperStyle={{ color: '#a1a1aa', fontSize: 11 }} />
                <Area yAxisId="pct" type="monotone" dataKey="negative" stackId="s" stroke="#f43f5e" fill="#f43f5e" fillOpacity={0.7} strokeWidth={0} name="negative" />
                <Area yAxisId="pct" type="monotone" dataKey="neutral"  stackId="s" stroke="#71717a" fill="#71717a" fillOpacity={0.45} strokeWidth={0} name="neutral" />
                <Area yAxisId="pct" type="monotone" dataKey="positive" stackId="s" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} strokeWidth={0} name="positive" />
                <Line yAxisId="vol" type="monotone" dataKey="volume" stroke="#818cf8" strokeWidth={2} strokeDasharray="5 3" dot={false} name="volume" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Alert Frequency — interactive stacked bars → alert feed with workflow */}
          <div className="glass-panel rounded-xl p-5">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-amber-400" />
                <h2 className="text-white font-semibold">Alert Frequency</h2>
              </div>
              <span className="text-xs text-zinc-500">Click any bar to view alert feed</span>
            </div>
            <p className="text-zinc-500 text-xs mb-4">
              Total: <span className="text-white font-semibold">{totalAlerts}</span> alerts · {criticals} critical
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={freqData} margin={{ top: 4, right: 16, bottom: 0, left: -20 }} style={{ cursor: 'pointer' }}
                onClick={d => { if (d?.activeLabel) setSelectedAlertDate(p => p === d.activeLabel ? null : d.activeLabel as string); }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis dataKey="date" stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 11 }} />
                <Tooltip {...TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ color: '#a1a1aa', fontSize: 11 }} />
                <Bar dataKey="info"     stackId="a" fill="#06b6d4" name="Info" />
                <Bar dataKey="warning"  stackId="a" fill="#f59e0b" name="Warning" />
                <Bar dataKey="critical" stackId="a" fill="#f43f5e" name="Critical" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Phase 2: Alert feed with status workflow */}
            {selectedAlertDate && (
              <div className="mt-4 border-t border-zinc-700/50 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-white">
                    Alert Feed — <span className="text-violet-400">{selectedAlertDate}</span>
                  </p>
                  <button onClick={() => setSelectedAlertDate(null)} className="text-zinc-500 hover:text-white transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  {allAlerts.map(alert => (
                    <div key={alert.id} className={`rounded-lg p-3 border ${alert.severity === 'critical' ? 'bg-red-500/5 border-red-500/20' : alert.severity === 'warning' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-cyan-500/5 border-cyan-500/20'}`}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium capitalize ${SEVERITY_BADGE[alert.severity]}`}>{alert.severity}</span>
                        <span className="text-white text-sm font-medium">{alert.title}</span>
                        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLOR[alert.status] ?? ''}`}>{alert.status}</span>
                      </div>
                      <p className="text-zinc-400 text-xs mb-2">{alert.claim}</p>
                      <div className="flex items-center gap-4 text-xs text-zinc-500 mb-2">
                        <span>Risk: <span className="text-rose-400 font-semibold">{alert.riskScore}</span></span>
                        <span>Velocity: <span className="text-amber-400 font-semibold">{alert.velocity}/hr</span></span>
                        <span>{alert.platforms.join(', ')}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {alert.actions.map((action, i) => (
                          <button key={i} className="text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 px-2 py-1 rounded transition-colors">{action}</button>
                        ))}
                        {/* Phase 2: Workflow status buttons */}
                        {alert.status === 'new' && (
                          <button onClick={() => setAlertStatuses(s => ({ ...s, [alert.id]: 'investigating' }))}
                            className="ml-auto text-xs bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 px-2 py-1 rounded transition-colors">
                            → Investigate
                          </button>
                        )}
                        {alert.status === 'investigating' && (
                          <>
                            <button onClick={() => setAlertStatuses(s => ({ ...s, [alert.id]: 'contained' }))}
                              className="ml-auto text-xs bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-2 py-1 rounded transition-colors">
                              ✓ Contained
                            </button>
                            <button onClick={() => setAlertStatuses(s => ({ ...s, [alert.id]: 'escalated' }))}
                              className="text-xs bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 px-2 py-1 rounded transition-colors">
                              ↑ Escalate
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phase 2: Geographic Risk Intelligence ───────────────────────── */}
      <div className="glass-panel rounded-xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            <h2 className="text-white font-semibold">Geographic Risk Intelligence</h2>
            <span className="text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-2 py-0.5 rounded-full">Phase 2</span>
          </div>
          <span className="text-xs text-zinc-500">Hover a region for detail · Bubble size ∝ alert count</span>
        </div>
        <GeoRiskMap regions={GEO_REGIONS} />
      </div>

      {/* ── Phase 2: Top Narratives — sortable + filterable + detail modal ─ */}
      <div className="glass-panel rounded-xl p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            <h2 className="text-white font-semibold">Top Narratives &amp; Viral Claims</h2>
            <span className="text-xs bg-zinc-800 text-zinc-400 border border-zinc-700 px-2 py-0.5 rounded-full">{filteredNarratives.length} of {MOCK_NARRATIVES.length}</span>
          </div>
          <button onClick={exportCSV} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-emerald-400 bg-zinc-800/60 border border-zinc-700/40 hover:border-emerald-500/40 px-3 py-1.5 rounded-lg transition-colors">
            <Download className="w-3 h-3" /> Export CSV
          </button>
        </div>

        {/* Phase 2: Filter chips */}
        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-zinc-500 text-xs">Platform:</span>
            {PLATFORMS.map(p => (
              <button key={p} onClick={() => setFilterPlatform(p)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterPlatform === p ? 'bg-violet-500/20 border-violet-500/40 text-violet-400' : 'bg-zinc-800/60 border-zinc-700/40 text-zinc-400 hover:border-violet-500/30'}`}>
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Target className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-zinc-500 text-xs">Tier:</span>
            {TIERS.map(t => (
              <button key={t} onClick={() => setFilterTier(t)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterTier === t ? 'bg-violet-500/20 border-violet-500/40 text-violet-400' : 'bg-zinc-800/60 border-zinc-700/40 text-zinc-400 hover:border-violet-500/30'}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Eye className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-zinc-500 text-xs">Status:</span>
            {STATUSES.map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors capitalize ${filterStatus === s ? 'bg-violet-500/20 border-violet-500/40 text-violet-400' : 'bg-zinc-800/60 border-zinc-700/40 text-zinc-400 hover:border-violet-500/30'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {filteredNarratives.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3 opacity-60" />
            <p className="text-zinc-400 text-sm font-medium">No narratives match the active filters</p>
            <p className="text-zinc-600 text-xs mt-1">Your monitoring posture is effective for these criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-700/50">
                  {[
                    { key: null,             label: 'Claim' },
                    { key: 'riskScore',      label: 'Risk' },
                    { key: 'velocity',       label: 'Velocity' },
                    { key: null,             label: 'Platforms' },
                    { key: 'reachEstimate',  label: 'Reach' },
                    { key: null,             label: 'Status' },
                    { key: null,             label: '' },
                  ].map((h, i) => (
                    <th key={i} className={`text-left py-2 pr-4 text-zinc-400 text-xs font-semibold uppercase tracking-wider ${h.key ? 'cursor-pointer select-none hover:text-white' : ''}`}
                      onClick={() => h.key && toggleSort(h.key as typeof sortBy)}>
                      <span className="flex items-center gap-1">
                        {h.label}{h.key && sortBy === h.key && <ArrowUpDown className="w-3 h-3" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {filteredNarratives.map(n => (
                  <tr key={n.id} className="hover:bg-zinc-800/30 transition-colors cursor-pointer" onClick={() => setSelectedNarrative(n)}>
                    <td className="py-3 pr-4 max-w-xs"><span className="text-zinc-200 text-xs leading-relaxed line-clamp-2">{n.claim}</span></td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-sm ${n.riskScore >= 80 ? 'text-red-400' : n.riskScore >= 60 ? 'text-amber-400' : 'text-emerald-400'}`}>{n.riskScore}</span>
                        <div className="w-12 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${n.riskScore >= 80 ? 'bg-red-500' : n.riskScore >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${n.riskScore}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <span className="text-amber-400 font-semibold">{n.velocity}</span><span className="text-zinc-500 text-xs">/hr</span>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {n.platforms.map(p => <span key={p} className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded">{p}</span>)}
                      </div>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <span className="text-violet-400 font-semibold text-xs">
                        {n.reachEstimate >= 1000000 ? `${(n.reachEstimate/1000000).toFixed(1)}M` : `${(n.reachEstimate/1000).toFixed(0)}K`}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${STATUS_COLOR[n.status] ?? 'text-zinc-400 bg-zinc-700'}`}>{n.status}</span>
                    </td>
                    <td className="py-3">
                      <Eye className="w-3.5 h-3.5 text-zinc-500 hover:text-violet-400 transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Phase 3: Source & Amplifier Intelligence ────────────────────── */}
      <div className="glass-panel rounded-xl p-5">
        <button className="w-full flex items-center justify-between" onClick={() => setShowAmplifiers(s => !s)}>
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-rose-400" />
            <h2 className="text-white font-semibold">Source &amp; Amplifier Intelligence</h2>
            <span className="text-xs bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2 py-0.5 rounded-full">Phase 3</span>
            <span className="text-xs text-zinc-500 ml-1">Top {MOCK_AMPLIFIERS.length} high-risk amplifier accounts</span>
          </div>
          {showAmplifiers ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        {showAmplifiers && <div className="mt-4"><AmplifiersPanel amplifiers={MOCK_AMPLIFIERS} /></div>}
      </div>

      {/* ── Phase 3: ROI & Value Calculator ─────────────────────────────── */}
      <div className="glass-panel rounded-xl p-5">
        <button className="w-full flex items-center justify-between" onClick={() => setShowROI(s => !s)}>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <h2 className="text-white font-semibold">ROI &amp; Value Calculator</h2>
            <span className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full">Phase 3</span>
            <span className="text-xs text-zinc-500 ml-1">Estimate annual savings &amp; reach protected</span>
          </div>
          {showROI ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        {showROI && <div className="mt-5"><ROICalculator /></div>}
      </div>

      {/* Phase 2: Narrative detail modal */}
      {selectedNarrative && (
        <NarrativeModal
          narrative={{ ...selectedNarrative, status: narrativeStatuses[selectedNarrative.id] ?? selectedNarrative.status }}
          onClose={() => setSelectedNarrative(null)}
          onStatusChange={(id, status) => setNarrativeStatuses(s => ({ ...s, [id]: status }))}
        />
      )}

    </div>
  );
};

export { Analytics };
export default Analytics;

